import { useMemo, useState } from "react";
import { buildReportHtml, buildReportHtmlV2, calcMetrics, type OwnerProfile,
  type AgentProfile,
  type ShopRow,
  type TxRow,
  type Allocation,
  type WeekDay, } from "../../lib/dailyReportEmail";


// ── Mock data ────────────────────────────────────────────────────────────
const OWNER: OwnerProfile = {
  id: "o1",
  name: "Jane",
  business_name: "Epic Enterprise Traders",
  internal_email: "jane@acme.co.ke",
};

const MOCK_WEEK: WeekDay[] = [
  { label: "Tue", date: "2026-09-23", revenue: 18400, isToday: false },
  { label: "Wed", date: "2026-09-24", revenue: 21200, isToday: false },
  { label: "Thu", date: "2026-09-25", revenue: 15800, isToday: false },
  { label: "Fri", date: "2026-09-26", revenue: 27600, isToday: false },
  { label: "Sat", date: "2026-09-27", revenue: 32100, isToday: false },
  { label: "Sun", date: "2026-09-28", revenue: 12400, isToday: false },
  { label: "Mon", date: "2026-09-29", revenue: 43200, isToday: true  },
];

const AGENTS: AgentProfile[] = [
  { id: "a1", name: "John Kamau",    agent_id: "AGT-0001", zone: "Westlands", target: 20000 },
  { id: "a2", name: "Mary Wanjiru",  agent_id: "AGT-0002", zone: "Karen",     target: 15000 },
  { id: "a3", name: "Peter Otieno",  agent_id: "AGT-0003", zone: "Kilimani",  target: 15000 },
  { id: "a4", name: "Grace Njeri",   agent_id: "AGT-0004", zone: "CBD",       target: 12000 },
  { id: "a5", name: "Samuel Mwangi", agent_id: "AGT-0005", zone: "Eastlands", target: 12000 },
];

const SHOPS: ShopRow[] = [
  { id: "s1", name: "Kibera Main",  shop_code: "SHP-0001" },
  { id: "s2", name: "Ngong Road",   shop_code: "SHP-0002" },
];

type MockSeed = {
  minAgo: number;
  amount: number;
  qty: number;
  product: string;
  sku: string;
  cost: number;          // total buying cost for this line
  method: "cash" | "mpesa" | "split" | "credit";
  cash?: number;
  mpesa?: number;
  credit?: boolean;
};

function t(s: MockSeed) {
  const cash  = s.cash  ?? (s.method === "cash"  ? s.amount : 0);
  const mpesa = s.mpesa ?? (s.method === "mpesa" ? s.amount : 0);
  return {
    id: crypto.randomUUID(),
    created_at: new Date(Date.now() - s.minAgo * 60_000).toISOString(),
    amount: s.amount,
    quantity: s.qty,
    product_name: s.product,
    product_sku:  s.sku,
    cost: s.cost,
    payment_method: s.method,
    cash_amount:  cash,
    mpesa_amount: mpesa,
    credit_sale_id: s.credit ? crypto.randomUUID() : null,
  };
}

const TODAY_TXNS: TxRow[] = [
  { ...t({ minAgo: 30,  amount: 12500, qty: 25, product: "Cooking Oil 5L", sku: "OIL-5L", cost: 9500,  method: "mpesa" }),
    source: "agent", actor_id: "a1", actor_name: "John Kamau",   actor_code: "AGT-0001", seller_id: null, seller_name: null },

  { ...t({ minAgo: 55,  amount: 3400,  qty: 12, product: "Sugar 2kg",      sku: "SGR-2K", cost: 2400,  method: "cash"  }),
    source: "agent", actor_id: "a1", actor_name: "John Kamau",   actor_code: "AGT-0001", seller_id: null, seller_name: null },

  { ...t({ minAgo: 80,  amount: 9800,  qty:  8, product: "Rice 10kg",      sku: "RCE-10", cost: 7200,  method: "split", cash: 4000, mpesa: 5800 }),
    source: "agent", actor_id: "a2", actor_name: "Mary Wanjiru", actor_code: "AGT-0002", seller_id: null, seller_name: null },

  { ...t({ minAgo: 120, amount: 7200,  qty: 20, product: "Detergent 1kg",  sku: "DTR-1K", cost: 4800,  method: "credit", credit: true }),
    source: "agent", actor_id: "a3", actor_name: "Peter Otieno", actor_code: "AGT-0003", seller_id: null, seller_name: null },

  { ...t({ minAgo: 150, amount: 4100,  qty: 18, product: "Soap Bar",       sku: "SOP-BR", cost: 2600,  method: "cash"  }),
    source: "shop",  actor_id: "s1", actor_name: "Kibera Main",  actor_code: "SHP-0001", seller_id: "a1", seller_name: "John Kamau" },

  { ...t({ minAgo: 180, amount: 3300,  qty:  8, product: "Cooking Oil 5L", sku: "OIL-5L", cost: 2500,  method: "mpesa" }),
    source: "shop",  actor_id: "s1", actor_name: "Kibera Main",  actor_code: "SHP-0001", seller_id: "a4", seller_name: "Grace Njeri" },

  { ...t({ minAgo: 220, amount: 2900,  qty:  8, product: "Sugar 2kg",      sku: "SGR-2K", cost: 2100,  method: "credit", credit: true }),
    source: "shop",  actor_id: "s2", actor_name: "Ngong Road",   actor_code: "SHP-0002", seller_id: "a2", seller_name: "Mary Wanjiru" },
];

const YESTERDAY_TXNS: TxRow[] = TODAY_TXNS.map(x => ({ ...x, amount: Math.round(x.amount * 0.82) }));

const ALLOCATIONS: Allocation[] = [
  { id: "al1", agent_id: "a1", agent_name: "John Kamau",    product_id: "p1", product_name: "Cooking Oil 5L", product_sku: "OIL-5L", product_unit: "units", allocated: 100, remaining: 4 },
  { id: "al2", agent_id: "a2", agent_name: "Mary Wanjiru",  product_id: "p2", product_name: "Sugar 2kg",      product_sku: "SGR-2K", product_unit: "units", allocated: 80,  remaining: 12 },
  { id: "al3", agent_id: "a3", agent_name: "Peter Otieno",  product_id: "p3", product_name: "Rice 10kg",      product_sku: "RCE-10", product_unit: "bags",  allocated: 50,  remaining: 30 },
];

// ── Page ─────────────────────────────────────────────────────────────────
export default function ReportPreview() {
  const [variant, setVariant] = useState<"current" | "v2">("v2");

  const today  = useMemo(() => calcMetrics(TODAY_TXNS,     AGENTS, SHOPS, ALLOCATIONS), []);
  const yest   = useMemo(() => calcMetrics(YESTERDAY_TXNS, AGENTS, SHOPS, []),          []);

  const html = useMemo(() => {
    const args = {
      owner: OWNER,
      today, yest,
      dateLabel: new Date().toLocaleDateString("en-KE", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      }),
      agentCount: AGENTS.length,
      domain: "example.com",
    };
    return variant === "v2" ? buildReportHtmlV2({...args, weekTrend: MOCK_WEEK}) : buildReportHtml(args);
  }, [variant, today, yest]);

  return (
    <div style={{ padding: 20, background: "#0a0d14", minHeight: "100vh" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {(["current", "v2"] as const).map(v => (
          <button
            key={v}
            onClick={() => setVariant(v)}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "1px solid #1f2937",
              background: variant === v ? "#1e293b" : "#111827",
              color: variant === v ? "#f9fafb" : "#9ca3af",
              fontFamily: "monospace", fontSize: 12, cursor: "pointer",
            }}>
            {v === "current" ? "Current design" : "V2 (clean)"}
          </button>
        ))}
      </div>

      <iframe
        title="Report preview"
        srcDoc={html}
        style={{
          width: "100%", height: "calc(100vh - 120px)",
          border: "1px solid #1f2937", borderRadius: 12, background: "#fff",
        }}
      />
    </div>
  );
}